import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, FileText, Building2, Truck, ArrowRight, CheckSquare } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useAuth } from '../contexts/AuthContext';

/**
 * Top-bar bell. Two feeds in one place:
 *   - tasks a teammate assigned to you (everyone)
 *   - pending approvals (admin / accounting / office manager)
 */
export function NotificationBell({ pending, userRole }) {
  const { token, API } = useAuth();
  const [inbox, setInbox] = useState(null);

  const loadInbox = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tasks/inbox`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInbox(data);
    } catch { setInbox(null); }
  }, [API, token]);

  useEffect(() => {
    if (!token) return undefined;
    loadInbox();
    const timer = setInterval(loadInbox, 120000);
    return () => clearInterval(timer);
  }, [token, loadInbox]);

  const approvals = pending?.total || 0;
  const myTasks = inbox?.delegated_to_me || 0;
  const total = approvals + myTasks;
  if (total === 0) return null;

  const canSeeCustomers = ['admin', 'accounting'].includes(userRole);
  // Office managers approve rate changes too, and since the submission email
  // now goes to billing rather than to admins, the badge is how admins see them.
  const canSeeRateChanges = ['admin', 'accounting', 'office_manager'].includes(userRole);

  const items = [];
  if (canSeeCustomers && pending?.customers > 0) {
    items.push({
      key: 'customers',
      icon: Building2,
      label: `${pending.customers} new customer${pending.customers > 1 ? 's' : ''} pending`,
      to: '/approvals?tab=customers',
      color: 'text-blue-700 bg-blue-50',
    });
  }
  if (pending?.carriers > 0) {
    items.push({
      key: 'carriers',
      icon: Truck,
      label: `${pending.carriers} new carrier${pending.carriers > 1 ? 's' : ''} pending`,
      to: '/approvals?tab=carriers',
      color: 'text-amber-700 bg-amber-50',
    });
  }
  if (canSeeRateChanges && pending?.rate_changes > 0) {
    items.push({
      key: 'rate_changes',
      icon: FileText,
      label: `${pending.rate_changes} rate change${pending.rate_changes > 1 ? 's' : ''} pending`,
      to: '/approvals?tab=rate_changes',
      color: 'text-red-700 bg-red-50',
    });
  }

  return (
    <Popover onOpenChange={(open) => open && loadInbox()}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
          data-testid="notifications-btn"
          aria-label={`${total} item${total > 1 ? 's' : ''} needing attention`}
        >
          <Bell className="h-5 w-5" />
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {total > 99 ? '99+' : total}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-dropdown">
        {myTasks > 0 && (
          <div data-testid="notif-tasks-section">
            <div className="border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">Assigned to you</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {myTasks} task{myTasks > 1 ? 's' : ''} from your teammates
                {inbox?.overdue > 0 && <span className="text-red-600"> · {inbox.overdue} overdue</span>}
              </p>
            </div>
            <div className="py-1">
              {(inbox?.recent || []).map((task) => (
                <Link
                  key={task.id}
                  to="/tasks"
                  className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors"
                  data-testid={`notif-task-${task.id}`}
                >
                  <div className="h-8 w-8 rounded-full flex items-center justify-center text-slate-700 bg-slate-100 shrink-0">
                    <CheckSquare className="h-4 w-4" />
                  </div>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-slate-700 truncate">{task.title}</span>
                    <span className="block text-[11px] text-slate-400">
                      from {task.assigned_by_name}
                      {task.due_date ? ` · due ${task.due_date}` : ''}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
            <div className="border-t border-slate-100 px-4 py-2 bg-slate-50">
              <Link to="/tasks" className="text-xs font-medium text-blue-600 hover:text-blue-800"
                    data-testid="notif-view-tasks">
                Open Tasks Dashboard →
              </Link>
            </div>
          </div>
        )}

        {items.length > 0 && (
          <div data-testid="notif-approvals-section">
            <div className="border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">Pending Approvals</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {approvals} item{approvals > 1 ? 's' : ''} awaiting review
              </p>
            </div>
            <div className="py-1">
              {items.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                  data-testid={`notif-${item.key}`}
                >
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center ${item.color}`}>
                    <item.icon className="h-4 w-4" />
                  </div>
                  <span className="flex-1 text-sm text-slate-700">{item.label}</span>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </Link>
              ))}
            </div>
            <div className="border-t border-slate-100 px-4 py-2 bg-slate-50">
              <Link
                to="/approvals"
                className="text-xs font-medium text-blue-600 hover:text-blue-800"
                data-testid="notif-view-all"
              >
                View all approvals →
              </Link>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
